import { cn } from 'cn';
import { type ReactNode, useId } from 'react';

import { type UserListItem } from '~/data/user';

import { isFieldRequired, unique } from '@vestigas/utils';

import {
  InputLabel,
  type Option,
  ValidationError,
} from '@vestigas/ui/molecules';

import { MultiSelectUsers } from '../SelectServerDriven/MultiSelectUsers';

import { useFieldContext } from './form-context';

type P = {
  readonly classNameErrors?: string;
  readonly filterActive?: boolean | null;
  readonly formatOptionLabel?: (option: Option<UserListItem>) => ReactNode;
  readonly hint?: string;
  readonly isClearable?: boolean;
  readonly isDisabled?: boolean;
  readonly isMultiSelect?: boolean;
  readonly isRemovableValue?: (data: Option<UserListItem>) => boolean;
  readonly label?: string;
  readonly placeholder?: string;
} & ComponentStyling;

export const MultiSelectUsersField = ({
  className,
  classNameErrors,
  filterActive = true,
  formatOptionLabel,
  hint,
  isClearable = true,
  isDisabled = false,
  isMultiSelect = true,
  isRemovableValue,
  label,
  placeholder,
  style,
}: P) => {
  const field = useFieldContext();

  const isRequired = isFieldRequired(
    field.options.form.options.validators,
    field.name,
  );

  const id = useId();

  const errors = unique(
    field.state.meta.errors.map(({ message }) => message),
  ).join(', ');
  const hasErrors = Boolean(errors);

  return (
    <div className={cn('relative', className)} style={style}>
      {Boolean(label) && (
        <InputLabel hint={hint} htmlFor={id} isRequired={isRequired}>
          {label}
        </InputLabel>
      )}
      <MultiSelectUsers
        filterActive={filterActive}
        formatOptionLabel={formatOptionLabel}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isMultiSelect={isMultiSelect}
        isRemovableValue={isRemovableValue}
        placeholder={placeholder}
        value={[field.state.value].flat().filter(Boolean)}
        onChange={(users) => {
          if (Array.isArray(users)) {
            field.handleChange(users.map(({ id: userId }) => userId));
          } else {
            field.handleChange(users?.id ?? '');
          }
        }}
      />
      {hasErrors ? (
        <ValidationError className={classNameErrors}>{errors}</ValidationError>
      ) : null}
    </div>
  );
};
